/**
 * Layer-creation utilities — single source of truth for "build a new
 * layer / customization / package / marker from agent- or user-supplied
 * params". Lifted from `apps/web/src/app/_core/state/ad/ad.utils.ts`
 * so web, mcp-server, and any other layer-creating surface share the
 * same defaults and semantics.
 *
 * Sharing model:
 *   - apps/web imports from this file directly via relative path (its
 *     existing pattern for LayerDto, LayersUtils, CreativePackageUtils).
 *     `apps/web/src/app/_core/state/ad/ad.utils.ts` keeps thin static
 *     re-export shims so the 21 web call sites stay unchanged.
 *   - API MCP layer handlers use the API-owned defaults and creation helpers
 *     under `apps/api/src/mcp/runtime`.
 *
 * Defaults applied to agent-/user-created layers:
 *   - `style`: combined layout + visual defaults from `layer-defaults.ts`
 *     (single source). Caller-provided style overrides win when
 *     `enableStyles` is set.
 *   - `interactivity`: caller responsibility — no longer baked in here
 *     (see commit history for prior baked-in behavior). Caller passes
 *     `interactivityEnabled` to mark the layer as immediately editable.
 *
 * IDs: `Date.now()`-based timestamps (legacy — DB rows use this format).
 * `generateUniqueId()` returns a real UUID v4 via Web Crypto API
 * (`globalThis.crypto.randomUUID()`, available in Node 19+ and modern
 * browsers).
 *
 * This file has NO Angular, RxJS, or DOM dependencies — only pure data
 * transformation. DOM helpers (calculateDeepBounds, isInNavigationZone)
 * stay in the web-side `ad.utils.ts`.
 */

import { merge } from 'lodash';
import { LayerDto, LayerDuplicateOptions, LayerGroupDto, LayerType } from '../models/layer/layer.dto';
import { createLayoutGroupDefaults } from '../models/layer/layout-group.dto';
import { getDefaultLayoutStyles, getDefaultVisualStyles, getDefaultLayerStyle } from '../models/layer/layer-defaults';
import { CustomizationItemDto, CustomizationType } from '../models/customization/customization.dto';
import { CreativeUnitPackageDto, StylesContainerConfig } from '../models/creative-unit-package.dto';
import { DEFAULT_NEW_TEMPLATE_RENDER_SCALE } from '../models/advanced-config.dto';
import { CreativePackageUtils } from './creative-package.utils';
import { LayersUtils } from './layers.utils';
import { ExportType } from '../../encode/models/job-def.dto';
import { shapeGallery } from './shape-gallery.data';

export class LayerCreationUtils {
	// ───────────────────────────────────────────────────────────────────
	// Style defaults — re-exported here so web/mcp call sites that used to
	// do `AdUtils.getDefaultLayoutStyles(type)` still work via the shim.
	// The actual implementations live in `models/layer/layer-defaults.ts`
	// (the single source of truth, pre-existing).
	// ───────────────────────────────────────────────────────────────────

	public static getDefaultLayoutStyles(type: LayerDto['type']): any {
		return getDefaultLayoutStyles(type as string);
	}

	public static getDefaultVisualStyles(type: LayerDto['type']): any {
		return getDefaultVisualStyles(type as string);
	}

	public static getDefaultStyles(type: LayerDto['type']): any {
		return getDefaultLayerStyle(type as string);
	}

	// ───────────────────────────────────────────────────────────────────
	// ID generation
	// ───────────────────────────────────────────────────────────────────

	public static generateNewId(): string {
		return 'id_' + Math.floor(Math.random() * 1000);
	}

	public static generateUniqueId(): string {
		return globalThis.crypto.randomUUID();
	}

	// ───────────────────────────────────────────────────────────────────
	// Package factory
	// ───────────────────────────────────────────────────────────────────

	/**
	 * Creates a blank creative unit package entity.
	 */
	public static createCreativeUnitPackage(overrides?: any, cloneFrom?: CreativeUnitPackageDto): CreativeUnitPackageDto {
		let schema = {
			id: overrides?.id || new Date().getTime().toString(),
			name: overrides?.name || 'New Package',
			creativeUnits: [],
			layers: [],
			customizations: [],
			styles: CreativePackageUtils.defaultStyles,
			variables: {},
			animation: {},
			exportTypes: [ExportType.STATIC],
			isPinned: false,
			...overrides,
			advancedConfig: {
				disableCustomSizes: true,
				// New templates default to 2x super sampling (frontend creation only); legacy
				// templates have no stored value and keep rendering at 1x via the backend.
				renderScale: DEFAULT_NEW_TEMPLATE_RENDER_SCALE,
				...overrides?.advancedConfig
			}
		};

		if (cloneFrom) {
			schema = {
				...schema,
				creativeUnits: cloneFrom.creativeUnits,
				layers: cloneFrom.layers,
				customizations: cloneFrom.customizations,
				styles: cloneFrom.styles,
				variables: cloneFrom.variables,
				animationConfig: cloneFrom.animationConfig,
				interactivity: cloneFrom.interactivity,
				disableLasso: cloneFrom.disableLasso,
				printConfig: cloneFrom.printConfig,
				advancedConfig: cloneFrom.advancedConfig
			};
		}

		return schema;
	}

	// ───────────────────────────────────────────────────────────────────
	// Layer factory — the central piece this whole file exists for
	// ───────────────────────────────────────────────────────────────────

	/**
	 * Create a Creative Unit Layer.
	 *
	 * Behavior is preserved verbatim from the web-side AdUtils version
	 * so existing UI flows are unchanged. Agent-driven write tools
	 * (mcp-server) call this with `interactivityEnabled: true` so
	 * agent-created layers are immediately selectable in the canvas.
	 */
	public static createCreativeUnitLayer(
		overrides: any,
		interactivityEnabled: boolean,
		enableDefaultStyles?: boolean,
		enableStyles?: boolean
	): LayerDto {
		let schema: Partial<LayerDto> = {
			id: overrides?.id || new Date().getTime().toString(),
			label: overrides.label || 'New Layer',
			type: overrides.type || 'text',
			creativeUnitId: overrides.creativeUnitId || undefined,
			style: enableStyles
				? overrides.style
				: enableDefaultStyles
					? this.getDefaultStyles(overrides.type === 'smart-text' ? 'text' : overrides.type)
					: null
		};

		if (overrides.type === 'text' || overrides.type === 'smart-text') {
			schema.text = {
				content: 'Text'
			};
		}

		if (overrides.type === 'image') {
			schema = {
				...schema,
				image: {
					assetPath: enableStyles
						? (overrides.image?.assetPath ?? 'https://placehold.co/600x600/png?text=Image%20Placeholder')
						: 'https://placehold.co/600x600/png?text=Image%20Placeholder'
				},
				style: {
					...schema.style,
					objectFit: 'cover'
				}
			} as LayerDto;
		}

		if (overrides.type === 'image' && overrides.aspectRatio) {
			const baseWidth = LayerCreationUtils.parseNumericFromDimension(schema.style.width, 20);
			const baseHeight = LayerCreationUtils.parseNumericFromDimension(schema.style.height, 20);

			schema.style = {
				...schema.style,
				...LayerCreationUtils.aspectRatioStyle(baseWidth, baseHeight, overrides.aspectRatio),
				objectFit: 'cover'
			};
		}

		if (overrides.type === 'smart-text' || (overrides.type === LayerType.TEXT && overrides.text?.smart)) {
			// Change this back to a text type. Make it smart.
			overrides = {
				...overrides,
				type: LayerType.TEXT,
				text: {
					...overrides.text,
					smart: true
				}
			};
		}

		if (overrides.type === 'button') {
			overrides = {
				...overrides,
				type: LayerType.BUTTON,
				text: {
					content: 'Button',
					smart: true
				}
			};

			// Merge default styles with overrides to preserve layout properties (width, height, position, etc.)
			schema.style = enableStyles
				? { ...this.getDefaultStyles(LayerType.BUTTON), ...overrides.style }
				: this.getDefaultStyles(LayerType.BUTTON);
		}

		if (overrides.type === 'shape') {
			overrides = {
				...overrides,
				type: LayerType.SHAPE,
				shape: enableStyles ? (overrides.shape ?? shapeGallery[0]) : shapeGallery[0]
			};

			// Merge default styles with overrides to preserve layout properties (width, height, position, etc.)
			schema.style = enableStyles
				? { ...this.getDefaultStyles(LayerType.SHAPE), ...overrides.style }
				: this.getDefaultStyles(LayerType.SHAPE);
		}

		if (overrides.type === 'video') {
			schema.video = {
				assetPath:
					overrides.video?.assetPath ??
					'https://videoencoderstack-videoencoder5499e89e-1a5uzj84axall.s3.us-east-2.amazonaws.com/placeholder-video.mp4',
				autoPlay: true,
				loop: false,
				muted: true
			} as LayerDto['video'];
		}

		if (overrides.type === 'audio' || overrides.type === 'video') {
			schema.audioConfig = {
				volumeTag: 'music',
				integratedLoudness: -24,
				loudnessRange: 7,
				maxTruePeak: -2
			} as LayerDto['audioConfig'];
		}

		if (overrides.type === 'frame' || overrides.type === 'group' || overrides.type === 'selectionGroup') {
			schema['layers'] = [];
			schema['group'] = {
				flow: 'legacy',
				layout: createLayoutGroupDefaults()
			} as LayerGroupDto & { layout: ReturnType<typeof createLayoutGroupDefaults> };
		}

		// we need this before merge
		if (!enableStyles && (!enableDefaultStyles || !interactivityEnabled)) {
			delete overrides.style;
		}

		return merge(schema, overrides);
	}

	/**
	 * The width and height a layer takes for an image of this ratio.
	 *
	 * The mismatched units are what this has always emitted — correct on a square unit, wrong
	 * elsewhere. Preserved verbatim so lifting the maths out here changes nothing.
	 */
	public static aspectRatioStyle(baseWidth: number, baseHeight: number, aspectRatio: number): { width: string; height: string } {
		const horizontal = aspectRatio > 1;
		const width = horizontal ? baseWidth * aspectRatio : baseWidth;
		const height = horizontal ? baseHeight : baseHeight / aspectRatio;

		return { width: `${width}cqw`, height: `${height}cqh` };
	}

	/**
	 * The same style recomputed for a corrected ratio, recovering the base it was built from.
	 *
	 * Returns null for anything that is not the cq pair creation writes. That is a type guard,
	 * not proof of ownership — a canvas resize emits cq units too, so callers decide on the
	 * exact strings whether the layer is still theirs to move.
	 */
	public static rebaseAspectRatioStyle(
		style: { width?: string; height?: string },
		previousRatio: number,
		nextRatio: number
	): { width: string; height: string } | null {
		if (!style?.width?.endsWith('cqw') || !style?.height?.endsWith('cqh')) {
			return null;
		}

		const width = Number.parseFloat(style.width);
		const height = Number.parseFloat(style.height);

		// Spelled out rather than `this.`: web re-exports this method as a bare static property,
		// which rebinds `this` to the shim class.
		if (
			!Number.isFinite(width) ||
			!Number.isFinite(height) ||
			!LayerCreationUtils.isUsableRatio(previousRatio) ||
			!LayerCreationUtils.isUsableRatio(nextRatio)
		) {
			return null;
		}

		const baseWidth = previousRatio > 1 ? LayerCreationUtils.snap(width / previousRatio) : width;
		const baseHeight = previousRatio > 1 ? height : LayerCreationUtils.snap(height * previousRatio);

		return LayerCreationUtils.aspectRatioStyle(baseWidth, baseHeight, nextRatio);
	}

	/** A ratio that can size a box. Zero, negative, NaN and Infinity all produce nonsense. */
	private static isUsableRatio(ratio: number): boolean {
		return Number.isFinite(ratio) && ratio > 0;
	}

	/**
	 * Drops the float noise a divide leaves behind, so recovering the base of a 16:9 box
	 * gives back the 20 it was built from rather than 20.000000000000004.
	 */
	private static snap(value: number): number {
		return Number(value.toPrecision(12));
	}

	private static parseNumericFromDimension(dimensionValue: string, defaultValue: number): number {
		if (!dimensionValue || typeof dimensionValue !== 'string') {
			return defaultValue;
		}

		// Extract numeric part from the dimension string
		const match = dimensionValue.match(/^(-?\d+(?:\.\d+)?)/);

		if (!match || !match[1]) {
			return defaultValue;
		}

		const numericValue = parseFloat(match[1]);

		return isNaN(numericValue) ? defaultValue : numericValue;
	}

	// ───────────────────────────────────────────────────────────────────
	// Customization factories
	// ───────────────────────────────────────────────────────────────────

	/**
	 * Create a Creative Unit Customization.
	 */
	public static createCreativeUnitCustomization(overrides: any): CustomizationItemDto {
		let schema: Partial<CustomizationItemDto> = {
			id: overrides?.id || new Date().getTime().toString(),
			type: overrides.type || 'text',
			label: overrides.label || 'New Customization',
			options: undefined
		};

		if (
			overrides.type === 'text' ||
			overrides.type === 'image-picker' ||
			overrides.type === 'video-picker' ||
			overrides.type === 'color-picker'
		) {
			schema.layerIds = [];
		}

		if (overrides.type === 'select') {
			schema.options = [];
		}

		if (overrides.type === 'toggle') {
			schema.options = [
				{
					id: 'true',
					label: 'True',
					value: true.toString(),
					layers: []
				},
				{
					id: 'false',
					label: 'False',
					value: false.toString(),
					layers: []
				}
			];
		}

		if (overrides.type === 'toggle') {
			schema.label = 'New Toggle';
		}

		if (overrides.type === 'image-picker') {
			schema.label = 'New Image Picker';
			schema['dataSources'] = ['uploads'];
			schema['showSelectButton'] = true;
			schema['selectButtonLabel'] = 'Select Image';
		}

		if (overrides.type === 'video-picker') {
			schema.label = 'New Video Picker';
			schema['dataSources'] = ['uploads'];
		}

		if (overrides.type === 'audio-picker') {
			schema.label = 'New Audio Picker';
			schema['dataSources'] = ['uploads'];
		}

		if (overrides.type === 'color-picker') {
			schema.label = 'New Color Picker';
			schema.options = [];
			schema['userColors'] = [];
		}

		if (overrides.type === 'frame' || overrides.type === 'group' || overrides.type === 'selectionGroup') {
			schema['children'] = [];
		}

		return {
			...schema,
			...overrides
		};
	}

	/**
	 * Duplicate a Creative Unit Customization.
	 *
	 * Groups nest without a depth limit (`CustomizationGroupDto` lists itself in its own
	 * discriminator subTypes), so this recurses the whole subtree. Re-IDing only the direct
	 * children left every grandchild carrying its original id — and, because each child was
	 * copied with a shallow spread, sharing its `children` array with the original. Every
	 * lookup in the app matches by id (merge-tag state is a flat last-wins map; delete filters
	 * by id; the two write paths disagree on first-match vs all-match), so a nested duplicate
	 * silently wired the copy and the original together.
	 *
	 * Deep-clone first, then walk: ids are hierarchical (`<root>/<i>/<j>`), the `/` delimiter
	 * keeping them unambiguous at any depth. The root id carries entropy the same way
	 * `duplicateLayer` does, so two duplicates in the same millisecond cannot collide.
	 */
	public static duplicateCreativeUnitCustomization(customization: any): CustomizationItemDto {
		const id = `${new Date().getTime().toString()}${Math.floor(Math.random() * 1000)}`;

		// Deep clone up front: this is what severs every shared reference (children, options,
		// layerIds, mappings, visibilityConditions, value) between the copy and the original.
		const duplicate: any = unfreeze(customization);

		// old id -> new id, for the reference remap below. A package already corrupted by the
		// old one-level behavior can hold repeated ids; those collapse here, which is the best
		// that can be done without inventing a disambiguation rule.
		const idsMap: Record<string, string> = {};

		const reId = (node: any, newId: string, isRoot: boolean) => {
			if (node?.id) {
				idsMap[node.id] = newId;
			}

			node.id = newId;
			node.label = (node?.label || (isRoot ? 'Customization' : 'Child')) + '-copy';

			if (node.type === CustomizationType.GROUP && node?.children?.length) {
				node.children.forEach((child: CustomizationItemDto, index: number) => reId(child, `${newId}/${index}`, false));
			} else {
				// Regression #10441: a stale `children` array makes a leaf look like a group and
				// drops it from the Create Variations dialog. Strip it at every depth.
				delete node.children;
			}
		};

		reId(duplicate, id, true);

		return remapCustomizationReferences(duplicate, idsMap) as CustomizationItemDto;
	}

	// ───────────────────────────────────────────────────────────────────
	// Misc factories
	// ───────────────────────────────────────────────────────────────────

	public static createStylesContainer(overrides: Partial<StylesContainerConfig>): StylesContainerConfig {
		return {
			id: overrides.id || new Date().getTime().toString(),
			name: overrides.name || 'Tab',
			styles: overrides.styles || ''
		};
	}

	/**
	 * Creates an Animation Timeline Marker.
	 */
	public static createMarker(overrides: any): any {
		const schema: any = {
			id: overrides?.id || new Date().getTime().toString(),
			label: overrides.label || 'New Marker',
			slug: stringToSlug(overrides.label || 'New Marker'),
			time: overrides.time || 0
		};

		return schema;
	}

	// ───────────────────────────────────────────────────────────────────
	// Layer duplication
	// ───────────────────────────────────────────────────────────────────

	public static duplicateLayer(
		layer: LayerDto,
		layers: LayerDto[],
		parentId?: string,
		interactivityEnabled?: boolean,
		enableDefaultStyles?: boolean,
		duplicateOptions?: LayerDuplicateOptions
	): { duplicatedLayer: LayerDto; updatedLayers: LayerDto[] } {
		const unfrozenLayer = unfreeze(layer);
		// Generate a unique ID based on timestamp and random number
		const id = duplicateOptions?.idsMap?.[layer.id] || `${new Date().getTime().toString()}${Math.floor(Math.random() * 1000)}`;

		const creativeUnitIdOverride =
			duplicateOptions?.customOverrides && 'creativeUnitId' in duplicateOptions.customOverrides
				? { creativeUnitId: duplicateOptions.customOverrides['creativeUnitId'] }
				: {};

		let overrides: LayerDto;

		if (duplicateOptions?.idsMap) {
			// Use the provided IDs map for duplication
			overrides = {
				...unfrozenLayer,
				id: id,
				layers: unfrozenLayer.layers?.length
					? LayersUtils.mapAllLayers(unfrozenLayer.layers, (layer, index) => {
							const newId = duplicateOptions.idsMap[layer.id] || id + '-' + index;
							return {
								...layer,
								...creativeUnitIdOverride,
								id: newId
							};
						})
					: undefined,
				...(duplicateOptions?.customOverrides || {})
			};
		} else {
			overrides = {
				...unfrozenLayer,
				id: id,
				label: unfrozenLayer.label + ' Copy',
				layers: unfrozenLayer.layers?.length
					? LayersUtils.mapAllLayersWithIdNesting(
							unfrozenLayer.layers,
							layer => {
								return {
									...layer,
									label: layer.label,
									...creativeUnitIdOverride
								};
							},
							id + '-'
						)
					: undefined,
				...(duplicateOptions?.customOverrides || {})
			};
		}

		let duplicatedLayer: LayerDto;

		if (duplicateOptions?.withProperties) {
			// skip root layer style overrides for children layers, keep the rest for now
			// (creativeUnitId override, ...)
			const childLayerOverrides = duplicateOptions?.customOverrides
				? (({ style, ...rest }) => rest)(duplicateOptions.customOverrides)
				: undefined;

			const childrenLayersWithOverrides = overrides.layers?.length
				? LayersUtils.mapAllLayers(overrides.layers, (childLayer: LayerDto) => ({
						...childLayer,
						...(childLayerOverrides || {})
					}))
				: overrides.layers;

			duplicatedLayer = {
				...overrides,
				layers: childrenLayersWithOverrides
			};
		} else {
			duplicatedLayer = LayerCreationUtils.createCreativeUnitLayer(
				overrides,
				interactivityEnabled,
				enableDefaultStyles,
				duplicateOptions?.withStyles
			);
		}

		let updatedLayers: LayerDto[];
		if (parentId) {
			updatedLayers = LayersUtils.mapAllLayers(layers, layer => {
				if (layer.id === parentId) {
					return {
						...layer,
						layers: [...layer.layers, duplicatedLayer]
					};
				} else {
					return layer;
				}
			});
		} else {
			updatedLayers = [...layers, duplicatedLayer];
		}

		return {
			duplicatedLayer,
			updatedLayers
		};
	}

	/**
	 * Duplicates a package-level layer across the entire creative unit package.
	 * - Duplicates the given layer in the package's top-level `layers` array.
	 * - Builds a mapping of original → duplicated layer IDs (including nested).
	 * - Uses that ID map to duplicate the same layer in every creative unit
	 *   (`pkg.creativeUnits`) so IDs stay consistent across the package
	 *   definition and unit instances.
	 */
	public static duplicatePackageLayerOnAllUnits(
		pkg: CreativeUnitPackageDto,
		layer: LayerDto,
		parentId?: string
	): { updatedPackage: CreativeUnitPackageDto; duplicatedLayer: LayerDto } {
		const layerToDuplicate = LayersUtils.findLayer(pkg.layers, (l: LayerDto) => l.id === layer.id);
		const { duplicatedLayer, updatedLayers } = this.duplicateLayer(
			layerToDuplicate,
			pkg.layers,
			parentId,
			!!pkg.interactivity?.enabled,
			false,
			{ withStyles: false, withProperties: true }
		);

		// prepare ids map for all nested layers
		const idsMap: { [key: string]: string } = {};
		idsMap[layerToDuplicate.id] = duplicatedLayer.id;

		const flattenedOriginalLayers = LayersUtils.flattenLayers(layerToDuplicate?.layers) || [];
		const flattenedDuplicatedLayers = LayersUtils.flattenLayers(duplicatedLayer?.layers) || [];

		flattenedOriginalLayers.forEach((originalLayer: LayerDto, index: number) => {
			const dup = flattenedDuplicatedLayers[index];
			if (originalLayer && dup) {
				idsMap[originalLayer.id] = dup.id;
			}
		});

		const updatedPackage = {
			...pkg,
			layers: updatedLayers,
			creativeUnits: pkg.creativeUnits.map(unit => {
				const unitLayerToDuplicate = LayersUtils.findLayer(unit.layers, (l: LayerDto) => l.id === layer.id);
				if (!unitLayerToDuplicate) {
					return unit;
				}
				const { updatedLayers: unitUpdatedLayers } = LayerCreationUtils.duplicateLayer(
					unitLayerToDuplicate,
					unit.layers,
					parentId,
					!!pkg.interactivity?.enabled,
					false,
					{ withStyles: false, withProperties: true, idsMap }
				);
				return {
					...unit,
					layers: unitUpdatedLayers
				};
			})
		};

		return { updatedPackage, duplicatedLayer };
	}

	/**
	 * Copy one unit's layer tree into another unit.
	 * - Linked layers (anywhere in `packageLayers`, selection groups included) keep their id, because the id is the
	 *   link to the package layer, and go through `mapLinkedLayer`.
	 * - Unit-specific layers get a new id and `creativeUnitId`. Sharing the source id would bind the copy to every
	 *   customization of the source layer and list both copies as one in the layer pickers (#9235, #10635).
	 * Nothing that references the old id is remapped (customizations, `.l-<id>` styles, `animation.element`,
	 * `{{layers.<id>}}` merge tags).
	 */
	public static reownUnitLayers(
		layers: LayerDto[],
		packageLayers: LayerDto[],
		creativeUnitId: string,
		mapLinkedLayer: (layer: LayerDto) => LayerDto = layer => layer
	): LayerDto[] {
		const packageLayerIds = new Set(LayersUtils.findAllLayers(packageLayers || [], () => true).map(layer => layer.id));

		return LayersUtils.mapAllLayers(layers, (layer: LayerDto) =>
			packageLayerIds.has(layer.id) ? mapLinkedLayer(layer) : { ...layer, id: LayerCreationUtils.generateUniqueId(), creativeUnitId }
		);
	}
}

// ─────────────────────────────────────────────────────────────────────
// Inlined helpers — were imports from web-side utils. Inlined to avoid
// threading additional cross-app dependencies for trivial functions.
// ─────────────────────────────────────────────────────────────────────

/** Slugify a string (lowercase, ASCII, dash-separated). Mirrors `apps/web/src/app/_core/utils/string.utils.ts`. */
function stringToSlug(str: string): string {
	if (!str) return str;

	let s = str.replace(/^\s+|\s+$/g, ''); // trim
	s = s.toLowerCase();

	// remove accents, swap ñ for n, etc
	const from = 'àáäâèéëêìíïîòóöôùúüûñç·/_,:;';
	const to = 'aaaaeeeeiiiioooouuuunc------';
	for (let i = 0, l = from.length; i < l; i++) {
		s = s.replace(new RegExp(from.charAt(i), 'g'), to.charAt(i));
	}

	s = s
		.replace(/[^a-z0-9 -]/g, '') // remove invalid chars
		.replace(/\s+/g, '-') // collapse whitespace and replace by -
		.replace(/-+/g, '-'); // collapse dashes

	return s;
}

/** Deep-clone via JSON round-trip. Mirrors `apps/web/src/app/_core/utils/object.utils.ts:unfreeze`. */
function unfreeze<T>(obj: T): T {
	return JSON.parse(JSON.stringify(obj));
}

/**
 * Rewrite customization references inside a freshly duplicated subtree so they point at the
 * copy rather than at the original.
 *
 */
function remapCustomizationReferences<T>(subtree: T, idsMap: Record<string, string>): T {
	if (!Object.keys(idsMap).length) {
		return subtree;
	}

	// One pass, looking each id up rather than running a replace per id. Matching the whole id
	// greedily is what keeps `123` from corrupting `123/0` or `1234`, and resolving through the
	// map means a replacement can never be rewritten again by a later id's turn.
	const json = JSON.stringify(subtree).replace(/customizations\.([\w/-]+)/g, (match, oldId) => {
		const newId = idsMap[oldId];

		return newId ? `customizations.${newId}` : match;
	});

	return JSON.parse(json);
}
